/**
 * DetailedRunLog — the complete record of a run: every step the workflow
 * declares, in the order it is expected to run them, with what actually
 * happened folded in.
 *
 * The main stream is the SUMMARY (phase + milestone). This is the COMPLETE
 * record, including the Epic login-spine rows that belong to no phase. It
 * lives ONLY inside `MoreInformationSection` — no step-level string is
 * rendered by any component in the primary body, and the must_pass
 * main-stream probe polices the other half of that rule.
 *
 * ORDER IS THE DECLARED MANIFEST'S. This reverses the component's original
 * rule ("order is the server's; never walk the registered manifest"), which
 * held while the log showed only what had already happened. Once never-run
 * steps were added they had to go somewhere, and appending them put a login
 * step that belongs at position 6 and an eDoc wait that belongs at 48 in a
 * block at the end, where the workflow would never have run them — so the
 * list stopped describing the workflow. The manifest is a sound spine for
 * this: its phases are declared "CONTIGUOUS … display order IS execution
 * order and no phase recurs", and its milestones in execution order.
 * `runLogModel.ts` owns the merge and documents what the reversal costs.
 *
 * Chronology rows the manifest does not declare (e.g. the graph wrapper) are
 * spliced in at their true position in time, never at an end.
 *
 * Three different absences, rendered differently (honest states):
 *   `chronology: []`          → "no history recorded yet"
 *   `chronology: null`/absent → "this run does not report a history"
 *   declared but never run    → skipped / not reached / upcoming, by position
 *
 * Each row is JUST a status dot plus the step label. Every other detail
 * (Started / Ended / Duration / Kind / State / Position / Step ID / Phase)
 * lives ONLY in the hover tooltip — nothing is duplicated inline. The tooltip
 * is built and rendered HERE, with the same `CustomTooltip` + `TooltipRowList`
 * primitives both apps already use, so ora and the plugin cannot drift: they
 * pass data and get an identical list. Row state is never fabricated — see
 * `runLogModel.ts` for exactly what each state is derived from.
 */

import type { ReactNode } from "react";
// `~/` (not a relative path) — the convention every other shared file under
// common/workflows uses for common/components, and the one both apps' bundlers
// and jest configs are set up to resolve.
import CustomTooltip from "~/common/components/CustomTooltip";
import { TooltipRowList } from "../../components/TooltipRowList";
import type { ChronologyRow } from "../../phaseModel";
import {
  formatDuration,
  formatElapsedSince,
  formatKind,
  formatTimestamp,
  type StepTooltipRow,
} from "../../stepTooltipParts";
import { STATE_TONE } from "../../components/WorkflowPhasePipeline";
import { DrawerSection } from "../DrawerSection";
import {
  buildRunLog,
  NEVER_RAN_STATES,
  RUN_LOG_STATE_EXPLANATIONS,
  type RunLogRow,
  type RunLogRowState,
  type RunLogStep,
} from "./runLogModel";

export const NO_HISTORY_YET = "No history recorded yet";
export const NO_HISTORY_REPORTED = "This run does not report a history";
export const NO_PHASE_MARKER = "No stage (login sequence)";

export type { RunLogRow, RunLogRowState, RunLogStep };

/** Dot tone per state. The three never-ran states share the gray tone and are
 *  told apart by the ring treatment below, so colour alone never carries the
 *  distinction (the drawer's standing a11y rule). */
const DOT_TONE: Record<RunLogRowState, string> = {
  completed: STATE_TONE.completed.dot,
  running: STATE_TONE.running.dot,
  failed: STATE_TONE.failed.dot,
  unfinished: STATE_TONE.pending.dot,
  skipped: STATE_TONE.pending.dot,
  not_reached: STATE_TONE.pending.dot,
  upcoming: STATE_TONE.pending.dot,
};

/**
 * Build the ordered tooltip rows for one log row. Rows whose value is
 * null/empty are dropped — the same contract as `buildTooltipRows`. Nothing
 * here is invented: a step that never ran genuinely has no Started/Ended/
 * Duration, so those rows are simply absent rather than shown as "—".
 */
export function buildRunLogTooltipRows(
  row: RunLogRow & { total: number },
): StepTooltipRow[] {
  const rows: StepTooltipRow[] = [];

  const startedFmt = formatTimestamp(row.startedAt);
  const endedFmt = formatTimestamp(row.endedAt);
  const duration = formatDuration(row.startedAt, row.endedAt);
  const elapsed = row.state === "running" ? formatElapsedSince(row.startedAt) : null;

  if (startedFmt) rows.push({ label: "Started", value: startedFmt });
  if (endedFmt) rows.push({ label: "Ended", value: endedFmt });
  if (duration) rows.push({ label: "Duration", value: duration });
  else if (elapsed) rows.push({ label: "Running for", value: elapsed });

  if (row.kind) rows.push({ label: "Kind", value: formatKind(row.kind) });
  rows.push({ label: "State", value: RUN_LOG_STATE_EXPLANATIONS[row.state] });
  rows.push({ label: "Position", value: `Step ${row.position} of ${row.total}` });
  if (row.stepId) rows.push({ label: "Step ID", value: row.stepId });
  // A step that never ran reports no phase grouping of its own; only say
  // "no phase" when the server actually marked the row exempt.
  const phaseValue = NEVER_RAN_STATES.has(row.state)
    ? [row.phaseLabel, row.milestoneLabel].filter(Boolean).join(" · ") || null
    : row.exempt
      ? NO_PHASE_MARKER
      : [row.phaseLabel, row.milestoneLabel].filter(Boolean).join(" · ") || null;
  rows.push({ label: "Stage", value: phaseValue });

  return rows.filter((r) => r.value !== null && r.value !== "");
}

export interface DetailedRunLogProps {
  /** `null` = the server reported no chronology; `[]` = it reported none yet. */
  chronology: ChronologyRow[] | null;
  /** The workflow's declared steps in declared order. Omit/empty and the log
   *  degrades to chronology-only — the pre-manifest behaviour, still honest. */
  manifest?: RunLogStep[];
  /** The substrate's live current-phase pointer. It can move ahead of the
   *  phase-history row, so a step with no chronology entry can still be the
   *  one running right now. */
  currentStepId?: string | null;
  /** Whether the instance has reached a terminal status. */
  isTerminal?: boolean;
  /** Whether it ended in a failure-like status (failed / rejected /
   *  timeout_cancelled). */
  isFailed?: boolean;
  /** Forwarded to the underlying DrawerSection. Defaults to false — starts
   *  collapsed, matching Run / Human-In-The-Loop Decisions / Artifacts. */
  defaultExpanded?: boolean;
  /** Forwarded to the underlying DrawerSection. localStorage key for the
   *  user's own expand/collapse choice. */
  persistKey?: string;
  /** Optional per-row decoration, rendered before the status dot. Added so the
   *  run record's evidence view can tag each row with who acted on it without
   *  maintaining a SECOND chronological list — it previously hand-rolled one
   *  purely because this component had no decoration slot. Omit it and the
   *  markup is byte-identical to before, which is what keeps the drawer's
   *  existing callers unaffected. */
  renderRowPrefix?: (row: RunLogRow) => ReactNode;
  /** Overrides the "Steps" heading on the underlying DrawerSection. */
  title?: string;
  /** Render the rows ALONE, with no DrawerSection around them. The run record
   *  puts this inside its own Evidence accordion, and an accordion inside an
   *  accordion is two disclosures for one thing. */
  bare?: boolean;
}

export function DetailedRunLog({
  chronology,
  manifest = [],
  currentStepId = null,
  isTerminal = false,
  isFailed = false,
  defaultExpanded = false,
  persistKey,
  renderRowPrefix,
  title = "Steps",
  bare = false,
}: DetailedRunLogProps) {
  const rows = buildRunLog({
    chronology,
    manifest,
    currentStepId,
    isTerminal,
    isFailed,
  });

  let body: ReactNode;

  if (rows == null) {
    body = (
      <p
        className="text-xs text-primary-gray-light-600"
        data-testid="detailed-run-log-absent"
      >
        {NO_HISTORY_REPORTED}
      </p>
    );
  } else if (rows.length === 0) {
    body = (
      <p
        className="text-xs text-primary-gray-light-600"
        data-testid="detailed-run-log-empty"
      >
        {NO_HISTORY_YET}
      </p>
    );
  } else {
    const total = rows.length;
    body = (
      <ol className="flex flex-col gap-1" data-testid="detailed-run-log">
        {rows.map((row) => {
          const neverRan = NEVER_RAN_STATES.has(row.state);
          const tooltipRows = buildRunLogTooltipRows({ ...row, total });
          const label = (
            <span
              className={
                row.state === "running"
                  ? "text-xs font-semibold text-primary-gray-light-900"
                  : neverRan
                    ? "text-xs text-primary-gray-light-500"
                    : "text-xs text-primary-gray-light-800"
              }
              // No native `title` fallback (story 28633): it put the
              // browser's bubble beside the CustomTooltip below, which is
              // the one tooltip — and that one opens on tap too.
            >
              {row.label}
            </span>
          );
          return (
            <li
              key={row.key}
              className="flex flex-row items-center gap-2 py-0.5"
              data-testid={
                neverRan ? "detailed-run-log-upcoming" : "detailed-run-log-entry"
              }
              data-exempt={row.exempt ? "true" : "false"}
              data-row-state={row.state}
              // The row's own identity. Display names are NOT unique — v2
              // declares three separate cold-wait gates all labelled "Resume
              // After Cold Wait" — so anything reading these rows back (a
              // test, someone reading the DOM) needs the step id to tell them
              // apart.
              data-step-id={row.stepId ?? undefined}
            >
              {renderRowPrefix?.(row) ?? null}
              <span
                aria-hidden="true"
                className={[
                  "h-[8px] w-[8px] flex-shrink-0 rounded-full",
                  // A step the run went past (or never reached) is drawn as a
                  // hollow ring, so "never happened" is distinguishable from
                  // "happened but unconfirmed" without adding inline text.
                  row.state === "skipped" || row.state === "not_reached"
                    ? "border border-primary-gray-light-400 bg-transparent"
                    : DOT_TONE[row.state],
                ].join(" ")}
              />
              <CustomTooltip
                id={`run-log-tooltip-${row.key}`}
                placement="top-start"
                arrow
                isOnModal
                title={<TooltipRowList rows={tooltipRows} />}
              >
                <span data-testid={`run-log-tooltip-trigger-${row.position - 1}`}>
                  {label}
                </span>
              </CustomTooltip>
            </li>
          );
        })}
      </ol>
    );
  }

  if (bare) return <>{body}</>;

  return (
    <DrawerSection
      title={title}
      defaultExpanded={defaultExpanded}
      persistKey={persistKey}
    >
      {body}
    </DrawerSection>
  );
}

export default DetailedRunLog;
